import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import {
  Shield, AlertTriangle, Phone, Eye, CheckCircle,
  Users, Car, MapPin, Mail, Siren, BadgeCheck,
} from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay },
});

const COLOR_CLASSES = {
  primary: { text: 'text-primary', bg: 'bg-primary-tint dark:bg-primary/10', border: 'border-primary/25' },
  teal: { text: 'text-teal-dark dark:text-teal', bg: 'bg-teal-tint dark:bg-teal/10', border: 'border-teal/25' },
  accent: { text: 'text-accent-dark dark:text-accent', bg: 'bg-accent-tint dark:bg-accent/10', border: 'border-accent/25' },
  ink: { text: 'text-ink dark:text-dark-ink', bg: 'bg-surface-alt dark:bg-white/10', border: 'border-line dark:border-dark-line' },
};

const SAFETY_TIPS = [
  { icon: Eye, title: 'Verify Your Driver', description: "Always check the driver's photo, name, and vehicle details in the app before getting in.", color: 'primary' },
  { icon: Phone, title: 'Share Your Trip', description: 'Share your real-time ride details with family or friends using our in-app share feature.', color: 'teal' },
  { icon: Shield, title: 'Stay Alert', description: 'Keep your phone charged and stay aware of your surroundings throughout the ride.', color: 'accent' },
  { icon: Car, title: 'Check Vehicle', description: 'Ensure the vehicle number plate and model match the details shown in your app.', color: 'ink' },
  { icon: Users, title: 'Travel Smart', description: 'Sit in the back seat and keep the doors unlocked until you feel completely safe.', color: 'primary' },
  { icon: CheckCircle, title: 'Report Issues', description: 'Report any safety concerns or misconduct immediately through our 24/7 support system.', color: 'teal' },
];

const EMERGENCY_STEPS = [
  { label: 'Press the emergency button in the app', icon: Siren },
  { label: 'Call local emergency services (112 / 102)', icon: Phone },
  { label: 'Contact TotoWala safety team: contact@totowala.in', icon: Mail },
  { label: 'Share your live location with trusted contacts', icon: MapPin },
];

const DRIVER_TIPS = [
  'Keep your vehicle in good condition and ensure all safety features work properly.',
  'Verify passenger identity before starting the trip and follow all traffic rules.',
  'Report any suspicious behaviour or safety concerns immediately through the app.',
  'Keep your app updated and GPS enabled for real-time tracking during every ride.',
];

const SafetyGuidelines = () => {
  return (
    <>
      <Seo
        title="Safety Guidelines - TotoWala | Safe Rides Across India"
        description="Learn about TotoWala's safety guidelines for riders and drivers. Our commitment to safe, secure, and reliable transportation across India."
        canonical="/safety"
        noIndex={false}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        {/* ── Hero ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.span
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5 bg-primary-tint dark:bg-primary/10 border border-primary/25 text-primary"
            >
              <Shield size={13} /> Safety First
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.06 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              Ride Safety Guidelines
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto mb-6"
            >
              Your safety is our highest priority. Follow these guidelines for a secure and comfortable ride experience on TotoWala.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.15 }}
              className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-control bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25"
            >
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              <span className="text-sm font-semibold text-red-700 dark:text-red-400">
                Emergency? Call <a href="tel:+918088257606" className="underline">+91 80882 57606</a>
              </span>
            </motion.div>
          </div>
        </section>

        {/* ── Safety tips ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-2">
              Essential Safety Tips
            </h2>
            <p className="text-base text-ink-soft dark:text-dark-ink-soft text-center max-w-md mx-auto mb-8">
              Simple steps that make every ride safer for both riders and drivers.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SAFETY_TIPS.map(({ icon: Icon, title, description, color }, i) => {
                const c = COLOR_CLASSES[color];
                return (
                  <motion.div key={title} {...fadeUp(i * 0.05)} className="p-6 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line">
                    <div className={`flex items-center justify-center w-11 h-11 rounded-control mb-4 ${c.bg} border ${c.border}`}>
                      <Icon size={20} className={c.text} />
                    </div>
                    <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-2">{title}</h3>
                    <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Emergency steps ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={26} className="text-white" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink dark:text-dark-ink mb-2">
                In Case of Emergency
              </h2>
              <p className="text-base text-ink-soft dark:text-dark-ink-soft">
                If you feel unsafe or encounter an emergency during your ride, follow these steps immediately.
              </p>
            </div>

            <div className="rounded-card bg-white dark:bg-dark-surface border border-red-200 dark:border-red-500/20 p-6 sm:p-8 flex flex-col gap-3.5">
              {EMERGENCY_STEPS.map(({ label, icon: Icon }, i) => (
                <motion.div key={label} {...fadeUp(i * 0.05)} className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-white">{i + 1}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-1 px-4 py-3 rounded-control bg-surface-alt dark:bg-white/5">
                    <Icon size={16} className="text-red-600 shrink-0" />
                    <span className="text-sm font-medium text-ink dark:text-dark-ink">{label}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── For drivers ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 items-start">

              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-primary-tint dark:bg-primary/10 border border-primary/25 text-primary">
                  Driver Safety
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink dark:text-dark-ink mb-6">
                  Guidelines for Drivers
                </h2>
                <div className="flex flex-col gap-3">
                  {DRIVER_TIPS.map((tip, i) => (
                    <motion.div key={tip} {...fadeUp(i * 0.05)} className="flex items-start gap-3 p-4 rounded-control bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line">
                      <span className="text-xs font-bold text-primary bg-primary-tint dark:bg-primary/10 rounded px-1.5 py-0.5 shrink-0 mt-0.5">0{i + 1}</span>
                      <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{tip}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              <motion.div {...fadeUp(0.1)} className="p-7 rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-5">
                  <Car size={26} className="text-white" />
                </div>
                <h3 className="font-display text-lg font-semibold text-ink dark:text-dark-ink mb-2.5">Driver Verification</h3>
                <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed mb-5">
                  All TotoWala drivers undergo comprehensive background checks and vehicle inspections before they can accept rides on the platform.
                </p>
                <div className="flex flex-col gap-2 mb-5">
                  {['ID & Licence Verified', 'Vehicle Inspected', 'Insurance Verified'].map((label) => (
                    <div key={label} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-control bg-white dark:bg-white/5 border border-line dark:border-dark-line">
                      <BadgeCheck size={15} className="text-primary shrink-0" />
                      <span className="text-sm font-semibold text-ink dark:text-dark-ink">{label}</span>
                      <span className="ml-auto w-2 h-2 rounded-full bg-teal" />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-ink-muted">
                  24/7 Driver Support: <a href="mailto:contact@totowala.in" className="text-primary font-medium">contact@totowala.in</a>
                </p>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── Need help ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Need Help?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-7 rounded-card bg-white dark:bg-dark-surface border border-red-200 dark:border-red-500/20 text-center">
                <div className="w-12 h-12 rounded-control bg-red-600 flex items-center justify-center mx-auto mb-4">
                  <Siren size={20} className="text-white" />
                </div>
                <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-1.5">Emergency Support</h3>
                <p className="text-sm text-ink-muted mb-5">For urgent safety situations during a ride</p>
                <a href="mailto:contact@totowala.in" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors">
                  <Mail size={14} /> contact@totowala.in
                </a>
              </div>
              <div className="p-7 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-center">
                <div className="w-12 h-12 rounded-control bg-primary flex items-center justify-center mx-auto mb-4">
                  <Phone size={20} className="text-white" />
                </div>
                <h3 className="text-base font-bold text-ink dark:text-dark-ink mb-1.5">General Safety</h3>
                <p className="text-sm text-ink-muted mb-5">Questions or non-urgent concerns</p>
                <a href="tel:+918088257606" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors">
                  <Phone size={14} /> +91 8088257606
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default SafetyGuidelines;
